/**
 * Watches all style tags scope and inline styles in the provided HTML string,
 * parsing them into inline styles inside `span` tags wrapped by their respective elements.
 *
 * @param htmlString - The raw HTML string containing tags, text, or `<style>` blocks.
 *
 * @example
 * // Scenario 1: Styles from <style> tags
 * cssStyleInlineParser("<style>div{color:red;}</style><div></div>");
 * // => <div><span style="color: red;"></span></div>
 *
 * // Scenario 2: Existing inline styles
 * cssStyleInlineParser("<div style='color:red;'></div>");
 * // => <div style=""><span style="color: red;"></span></div>
 */
export function cssStyleInlineParser(htmlString: string) {
  if (!htmlString) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  const styleTags = doc.querySelectorAll("style");

  styleTags.forEach((styleTag) => {
    const cssText = styleTag.textContent ?? "";

    const stylesheet = new CSSStyleSheet();
    try {
      stylesheet.replaceSync(cssText);

      const rules = stylesheet.cssRules;

      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];

        if (rule instanceof CSSStyleRule) {
          const seletor = rule.selectorText;
          const styleChain = rule.style.cssText;

          const targetElement = doc.querySelectorAll(seletor);
          targetElement.forEach((element) => {
            if (element instanceof HTMLElement) {
              if (element.tagName.toLowerCase() === "span") {
                element.style.cssText =
                  element.style.cssText + " " + styleChain;
                return;
              }

              const span = document.createElement("span");
              span.style.cssText = element.style.cssText + " " + styleChain;

              while (element.firstChild) {
                span.appendChild(element.firstChild);
              }

              element.style.cssText = "";
              element.appendChild(span);
            }
          });
        }
      }
    } catch (e) {
      console.error("Erro ao processar CSS dinâmico:", e);
    }

    styleTag.remove();
  });

  const elementsWithInlineStyles = doc.querySelectorAll("[style]");

  elementsWithInlineStyles.forEach((element) => {
    if (element instanceof HTMLElement) {
      if (element.tagName.toLowerCase() === "span") {
        return;
      }

      if (
        element.children.length === 1 &&
        element.firstElementChild?.tagName.toLowerCase() === "span"
      ) {
        return;
      }

      const styleChain = element.style.cssText;

      const span = document.createElement("span");
      span.style.cssText = styleChain;

      while (element.firstChild) {
        span.appendChild(element.firstChild);
      }

      element.style.cssText = "";
      element.appendChild(span);
    }
  });

  return doc.body.innerHTML;
}
